import {Search} from '@mui/icons-material';
import {debounce, OutlinedInput} from '@mui/material';
import {ChangeEvent, FC, useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {systemApi} from 'src/api/system';
import {useAppDispatch} from 'src/app/hooks';
import {AMPFormSelect} from 'src/components/common/AMPFormSelect';
import {FilterBar, FilterBarField} from 'src/components/common/FilterBar';
import {LocationType} from 'src/enums';
import {handleGetCountries} from 'src/hooks/useGetCountries';
import {filterInputSx, searchInputSx} from 'src/components/tables/tableToolbar.sx';
import {LocationsTableActionsType} from './LocationsTableActions.types';

const ALL = '';

/**
 * The locations toolbar.
 *
 * The filters used to live in a popover behind an icon, applied by a Submit button, and the only sign
 * one was set was that icon turning blue. They are on screen now and apply as they change: a filter
 * you can see is a filter you can trust, and an Apply button on four selects was a step that only
 * ever delayed the answer.
 */
export const LocationsTableActions: FC<LocationsTableActionsType> = ({
                                                                        handleSearch,
                                                                        handleFilter,
                                                                        title,
                                                                        icon,
                                                                        onAdd,
                                                                        addLabel,
                                                                        totalElements,
                                                                        filters,
                                                                    }) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const [getCountries, countries] = systemApi.useLazyGetCountriesQuery();

    const [country, setCountry] = useState<string>(ALL);
    const [city, setCity] = useState<string>(ALL);
    // The field's own text, so typing is not held up by the debounce that applies it.
    const [cityDraft, setCityDraft] = useState<string>(ALL);
    const [locationType, setLocationType] = useState<string>(ALL);

    useEffect(() => {
        void handleGetCountries(getCountries, dispatch);
    }, []);

    // Follows the page rather than owning the truth: a tile above the table sets a filter the bar
    // never saw, and the dropdown must not go on saying "all".
    useEffect(() => {
        if (!filters) {
            return;
        }
        setCountry(filters.country || ALL);
        setCity(filters.city || ALL);
        setCityDraft(filters.city || ALL);
        setLocationType(filters.locationType || ALL);
    }, [filters?.country, filters?.city, filters?.locationType]);

    const apply = (next: {country?: string; city?: string; locationType?: string}) => {
        const value = {country, city, locationType, ...next};
        setCountry(value.country);
        setCity(value.city);
        setLocationType(value.locationType);
        handleFilter({
            country: value.country,
            city: value.city,
            businessId: 0,
            locationType: value.locationType,
        });
    };

    const debouncedCity = useMemo(
        () => debounce((value: string) => apply({city: value}), 500),
        [country, locationType]
    );

    const countryOptions = useMemo(() => [
        {id: 0, name: intl.formatMessage({id: 'allCountries'}), value: ALL},
        ...(countries?.data ?? []).map((item, index) => ({
            id: index + 1,
            name: intl.formatMessage({id: item.name.toLowerCase()}),
            value: item.name,
        })),
    ], [countries?.data, intl]);

    const typeOptions = useMemo(() => [
        {id: 0, name: intl.formatMessage({id: 'allTypes'}), value: ALL},
        ...Object.values(LocationType).map((type, index) => ({
            id: index + 1,
            name: intl.formatMessage({id: type}),
            value: type,
        })),
    ], [intl]);

    const fields: FilterBarField[] = [
        {
            id: 'country',
            control: (
                <AMPFormSelect
                    dense
                    data={countryOptions}
                    value={country}
                    input="country"
                    // Cities belong to a country, so a city chosen under the old one would filter to
                    // nothing. Cleared here rather than left for the server to return an empty list.
                    onChange={(e) => apply({country: String(e.target.value), city: ALL})}
                />
            ),
        },
        {
            id: 'city',
            control: (
                // Free text, as it always was: there is no list of cities behind a country, only the
                // name somebody types. Debounced so it filters as they type rather than on blur.
                <OutlinedInput
                    sx={filterInputSx}
                    value={cityDraft}
                    disabled={!country}
                    placeholder={intl.formatMessage({id: 'city'})}
                    onChange={(event) => {
                        setCityDraft(event.target.value);
                        debouncedCity(event.target.value);
                    }}
                />
            ),
        },
        {
            id: 'locationType',
            control: (
                <AMPFormSelect
                    dense
                    data={typeOptions}
                    value={locationType}
                    input="locationType"
                    onChange={(e) => apply({locationType: String(e.target.value)})}
                />
            ),
        },
    ];

    return (
        <FilterBar
            title={title}
            icon={icon}
            onAdd={onAdd}
            addLabel={addLabel}
            subtitle={typeof totalElements === 'number'
                ? intl.formatMessage({id: 'rowsFound'}, {count: totalElements})
                : undefined}
            fields={fields}
            hasActiveFilters={Boolean(country || city || locationType)}
            onClearAll={() => {
                setCityDraft(ALL);
                apply({country: ALL, city: ALL, locationType: ALL});
            }}
            actions={(
                <OutlinedInput
                    sx={searchInputSx}
                    color="secondary"
                    type="text"
                    placeholder={intl.formatMessage({id: 'search'})}
                    name="search"
                    // Uncontrolled for the debounce, but seeded: the box has to say what the list is
                    // filtered by after a Back, which remounts it with the keyword still in the URL.
                    defaultValue={filters?.keyword ?? ''}
                    onChange={(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
                        debounce(() => handleSearch(event.target.value), 1000)()}
                    startAdornment={<Search/>}
                />
            )}
        />
    );
};
